/* eslint-disable simple-import-sort/imports */
import { ClickAwayListener } from '@mui/material'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import clsx from 'clsx'
import moment from 'moment'
import { ElementType, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { DayPicker } from 'react-day-picker'

import CalendarIcon from '../images/icons/calendar'
import useStyles from './styles'

import 'react-day-picker/dist/style.css'

interface Props {
  value: string
  onChange: (date: Date | null, value: string) => void
  placeholder?: string
  inputVariant?: TextFieldProps['variant']
  format?: string
  fullWidth?: boolean
  disabled?: boolean
  disablePast?: boolean
  className?: string
  keyboardIcon?: ElementType
  maxDate?: string | null
  minDate?: string | null
  error?: boolean
  helperText?: string
  momentFormat?: string
  isStatic?: boolean
  unavailableDates?: Array<string>
  disableSundays?: boolean
}

function SingleDatePicker({
  placeholder,
  format,
  value,
  onChange,
  className,
  disabled,
  disablePast,
  maxDate,
  minDate,
  error,
  helperText,
  momentFormat,
  fullWidth,
  isStatic = false,
  unavailableDates,
  disableSundays,
}: Props) {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)

  const handleCloseCalendar = useCallback(() => {
    setIsOpen(false)
  }, [])

  const formatToUse = momentFormat || format || 'MM-DD-YYYY'
  const maxMinDateFormat = 'YYYY-MM-DD'

  const validDate = useMemo(() => {
    if (!value) return undefined
    const parsed = moment(value, formatToUse, true)
    return parsed.isValid() ? parsed.toDate() : undefined
  }, [formatToUse, value])

  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    if (value) {
      const parsed = moment(value, formatToUse, true)
      return parsed.isValid() ? parsed.toDate() : new Date()
    }
    return new Date()
  })

  useEffect(() => {
    if (validDate) {
      setCurrentMonth(validDate)
    }
  }, [validDate])

  const validMaxDate = useMemo(() => {
    if (!maxDate) return undefined
    const parsed = moment(maxDate, maxMinDateFormat, true)
    return parsed.isValid() ? parsed.toDate() : undefined
  }, [maxDate, maxMinDateFormat])

  const validMinDate = useMemo(() => {
    if (!minDate) return undefined
    const parsed = moment(minDate, maxMinDateFormat, true)
    return parsed.isValid() ? parsed.toDate() : undefined
  }, [minDate, maxMinDateFormat])

  const disabledDays = useMemo(() => {
    const rules: Array<
      Date | { from?: Date; to?: Date; before?: Date; after?: Date; dayOfWeek?: number[] }
    > = []
    const today = moment().startOf('day')

    if (disablePast) {
      rules.push({ before: today.toDate() })
    }

    if (validMaxDate) {
      rules.push({ after: validMaxDate })
    }

    if (validMinDate) {
      rules.push({ before: validMinDate })
    }

    if (unavailableDates && unavailableDates.length) {
      unavailableDates.forEach(dateStr => {
        const date = moment(dateStr, 'YYYY-MM-DD').toDate()
        if (moment(date).isValid()) {
          rules.push(date)
        }
      })
    }

    if (disableSundays) {
      rules.push({ dayOfWeek: [0] })
    }

    return rules.length ? rules : undefined
  }, [disablePast, validMaxDate, validMinDate, unavailableDates, disableSundays])

  const handleDayClick = useCallback(
    (day: Date) => {
      if (!day) return onChange(null, '')
      const formattedValue = moment(day).format(formatToUse)
      onChange(day, formattedValue)
      if (!isStatic) {
        setIsOpen(false)
      }
    },
    [onChange, formatToUse, isStatic],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const parsed = moment(inputValue, formatToUse, true)
      if (parsed.isValid()) {
        const date = parsed.toDate()
        onChange(date, inputValue)
      } else if (!inputValue) {
        onChange(null, '')
      }
    },
    [onChange, formatToUse],
  )

  const displayValue = useMemo(() => {
    if (!value) return ''
    const parsed = moment(value, formatToUse, true)
    if (parsed.isValid()) {
      return parsed.format(formatToUse)
    }
    const dateParsed = moment(value)
    if (dateParsed.isValid()) {
      return dateParsed.format(formatToUse)
    }
    return value
  }, [value, formatToUse])

  if (isStatic) {
    return (
      <div className={clsx(className, classes.staticWrapper)}>
        <DayPicker
          className={clsx(classes.dayPicker, classes.staticDayPicker)}
          selected={validDate}
          onDayClick={handleDayClick}
          disabled={disabledDays as any}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
        />
      </div>
    )
  }

  return (
    <div className={clsx(className, classes.wrapper)}>
      <TextField
        fullWidth={fullWidth}
        placeholder={placeholder || 'MM-DD-YYYY'}
        value={displayValue}
        onChange={handleInputChange}
        onClick={() => !disabled && setIsOpen(true)}
        className={clsx(classes.input, classes.dateInput)}
        error={Boolean(error && helperText)}
        helperText={
          error && helperText ? <div className={classes.errorHandler}>{helperText}</div> : ''
        }
        InputProps={{
          endAdornment: <CalendarIcon />,
          classes: {
            input: classes.inputField,
          },
        }}
        disabled={disabled}
        FormHelperTextProps={{
          classes: {
            root: classes.helperText,
          },
        }}
      />
      {isOpen && !disabled && (
        <ClickAwayListener onClickAway={handleCloseCalendar}>
          <div className={classes.calendarWrapper}>
            <DayPicker
              className={classes.dayPicker}
              selected={validDate}
              onDayClick={handleDayClick}
              disabled={disabledDays as any}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
            />
          </div>
        </ClickAwayListener>
      )}
    </div>
  )
}

export default memo(SingleDatePicker)
